import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Grid,
  Typography,
  Divider,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  FormControlLabel,
  Switch,
  Tooltip,
  Stack,
  Avatar,
  CircularProgress,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { contractorsApi } from '../../services/api';

const ContractorStaffModal = ({ open, company, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [staff, setStaff] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    isActive: true
  });

  useEffect(() => {
    if (open && company) {
      fetchStaff();
    }
  }, [open, company]);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await contractorsApi.getStaff(company.id);
      setStaff(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch contractor staff:', error);
      setError('Failed to load contractor staff. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetFormData = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      isActive: true
    });
  };

  const resetForm = () => {
    resetFormData();
    setShowAddForm(false);
    setEditingUser(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddUser = () => {
    setShowAddForm(true);
    setEditingUser(null);
    resetFormData();
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.full_name || '',
      email: user.email || '',
      password: '',
      confirmPassword: '',
      isActive: user.is_active !== undefined ? user.is_active : true
    });
    setShowAddForm(true);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return false;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }

    if (!isValidEmail(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (!editingUser || formData.password.trim()) {
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters long');
        return false;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError('');

      const userData = {
        name: formData.name,
        email: formData.email,
        role: 'contractor',
        is_active: formData.isActive,
        contractor_company_id: company.id
      };

      if (formData.password.trim()) {
        userData.password = formData.password;
      }

      if (editingUser) {
        await contractorsApi.updateUser(editingUser.id, userData);
      } else {
        await contractorsApi.createStaff(company.id, userData);
      }

      await fetchStaff();
      resetForm();
    } catch (error) {
      console.error('Failed to save user:', error);
      setError(error.response?.data?.message || 'Failed to save user. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateUser = async (user) => {
    try {
      setSaving(true);
      setError('');
      
      await contractorsApi.deleteUser(user.id);
      await fetchStaff();
    } catch (error) {
      console.error('Failed to delete user:', error);
      setError('Failed to delete user. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setError('');
      resetForm();
      onClose();
    }
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { 
          height: '95vh',
          maxHeight: '95vh',
          display: 'flex',
          flexDirection: 'column',
          m: 1
        }
      }}
    >
      {/* Sticky Header */}
      <DialogTitle 
        sx={{ 
          p: 0,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ p: 3, pb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <PeopleIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                  Manage Staff
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create and manage user accounts for {company?.name}
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={handleClose} disabled={saving}>
              <CloseIcon />
            </IconButton>
          </Stack>

          {/* Company Summary Card */}
          <Paper 
            elevation={0}
            sx={{ 
              mt: 2,
              p: 2,
              bgcolor: 'warning.main',
              color: 'warning.contrastText',
              borderRadius: 2
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'warning.dark' }}>
                    <BusinessIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {company?.name}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Contact: {company?.contact_person} • {company?.email}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {staff.length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Staff Members
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </DialogTitle>

      {/* Scrollable Content */}
      <DialogContent 
        sx={{ 
          flex: 1,
          p: 0,
          overflow: 'auto'
        }}
      >
        <Box sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={4}>
            {/* Add/Edit User Form */}
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <Box 
                sx={{ 
                  p: 2, 
                  bgcolor: 'grey.50',
                  borderBottom: showAddForm ? '1px solid' : 'none',
                  borderColor: 'divider',
                  cursor: 'pointer'
                }}
                onClick={() => !showAddForm && setShowAddForm(true)}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ bgcolor: 'success.main', width: 32, height: 32 }}>
                      <PersonAddIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {showAddForm ? (editingUser ? 'Edit User' : 'Add New User') : 'Add Staff Member'}
                    </Typography>
                  </Stack>
                  <IconButton onClick={(e) => {
                    e.stopPropagation();
                    showAddForm ? resetForm() : setShowAddForm(true);
                  }}>
                    {showAddForm ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Stack>
              </Box>

              <Collapse in={showAddForm}>
                <Box sx={{ p: 3 }}>
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="name"
                          label="Full Name"
                          value={formData.name}
                          onChange={handleInputChange}
                          fullWidth
                          required
                          placeholder="Enter full name"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="email"
                          label="Email Address"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          fullWidth
                          required
                          placeholder="user@company.com"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="password"
                          label="Password"
                          type="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          fullWidth
                          required={!editingUser}
                          placeholder={editingUser ? "Leave empty to keep current password" : "Minimum 6 characters"}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="confirmPassword"
                          label="Confirm Password"
                          type="password"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          fullWidth
                          required={!editingUser || formData.password.trim()}
                          placeholder="Re-enter password"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Box sx={{ 
                          p: 2, 
                          bgcolor: 'grey.50', 
                          borderRadius: 1,
                          border: '1px dashed',
                          borderColor: 'grey.300'
                        }}>
                          <FormControlLabel
                            control={
                              <Switch
                                name="isActive"
                                checked={formData.isActive}
                                onChange={handleInputChange}
                                color="primary"
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  Active User
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Enable this user account for system access
                                </Typography>
                              </Box>
                            }
                          />
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Stack direction="row" spacing={2}>
                          <Button
                            type="submit"
                            variant="contained"
                            disabled={saving}
                            sx={{ minWidth: 140 }}
                          >
                            {saving ? (
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <CircularProgress size={16} color="inherit" />
                                <Typography variant="body2">Saving...</Typography>
                              </Stack>
                            ) : (
                              editingUser ? 'Update User' : 'Create User'
                            )}
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={resetForm}
                            disabled={saving}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </form>
                </Box>
              </Collapse>
            </Paper>

            {/* Staff List */}
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ p: 3, pb: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PeopleIcon color="primary" />
                    Current Staff ({staff.length})
                  </Typography>
                  {!showAddForm && staff.length > 0 && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddUser}
                      size="small"
                    >
                      Add Member
                    </Button>
                  )}
                </Stack>
              </Box>

              {loading ? (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  py: 6
                }}>
                  <CircularProgress size={32} sx={{ mr: 2 }} />
                  <Typography variant="h6">Loading staff members...</Typography>
                </Box>
              ) : staff.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Avatar sx={{ mx: 'auto', mb: 2, bgcolor: 'grey.100', width: 64, height: 64 }}>
                    <PersonIcon sx={{ fontSize: 32, color: 'grey.400' }} />
                  </Avatar>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No staff members found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Create contractor user accounts to manage work assignments.
                  </Typography>
                  {!showAddForm && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddUser}
                    >
                      Add First Staff Member
                    </Button>
                  )}
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {staff.map((user) => (
                        <TableRow key={user.id} hover>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                                <PersonIcon fontSize="small" />
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {user.full_name || 'No name'}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>{user.username || 'N/A'}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <Chip 
                              label={user.role?.charAt(0).toUpperCase() + user.role?.slice(1)} 
                              size="small" 
                              variant="outlined"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.is_active ? 'Active' : 'Inactive'}
                              color={user.is_active ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(user.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Tooltip title="Edit User">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditUser(user)}
                                  disabled={saving}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              {user.is_active && (
                                <Tooltip title="Delete User">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeactivateUser(user)}
                                    disabled={saving}
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Stack>
        </Box>
      </DialogContent>

      {/* Sticky Footer */}
      <DialogActions 
        sx={{ 
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          p: 3
        }}
      >
        <Button 
          onClick={handleClose} 
          disabled={saving}
          size="large"
          variant="outlined"
          sx={{ minWidth: 120 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContractorStaffModal; 